import { useMemo, useState } from "react";
import type { LaunchSummary } from "@bubblefun/shared";
import { logoSeed, relativeTime, usd, volumeUsd, volumeUsdValue } from "../lib/format";
import { publicMediaUrl } from "../lib/media";
import { Link } from "../lib/router";
import { MarketTable } from "./MarketTable";
import { TokenBadge } from "./TokenBadge";

type MarketSort = "trending" | "newest" | "mcap" | "volume";
type MarketView = "cards" | "list";

const sortOptions: Array<{ key: MarketSort; label: string }> = [
  { key: "trending", label: "Trending" },
  { key: "newest", label: "Newest" },
  { key: "mcap", label: "Market cap" },
  { key: "volume", label: "Volume" }
];

function MarketCard({ launch, index }: { launch: LaunchSummary; index: number }) {
  const isPositive = launch.change24h >= 0;
  const progress = Math.max(0, Math.min(100, launch.marketProgress));
  const graduated = launch.status === "milestone" || progress >= 100;
  const logo = publicMediaUrl(launch.logoUri);

  return (
    <Link
      href={`/token/${launch.address}`}
      className={`market-browser-card${isPositive ? " is-up" : " is-down"}`}
      aria-label={`${launch.name}, ${usd(launch.marketCapUsd)} market cap, ${isPositive ? "up" : "down"} ${Math.abs(launch.change24h).toFixed(1)}% in 24 hours`}
    >
      <span className={`market-browser-media market-browser-media--seed-${logoSeed(launch.symbol)}`}>
        <TokenBadge symbol={launch.symbol} size="lg" />
        {logo && <img src={logo} alt="" loading="lazy" onError={(event) => { event.currentTarget.hidden = true; }} />}
        <span className={`market-browser-state${graduated ? " is-graduated" : ""}`}><i />{graduated ? "Graduated" : "Live"}</span>
        <span className="market-browser-index">{String(index + 1).padStart(2, "0")}</span>
      </span>

      <span className="market-browser-card-body">
        <span className="market-browser-card-head">
          <span><strong>{launch.name}</strong><small>${launch.symbol}</small></span>
          <b className={isPositive ? "is-positive" : "is-negative"}>{isPositive ? "+" : ""}{launch.change24h.toFixed(1)}%</b>
        </span>

        <span className="market-browser-card-metrics">
          <span><small>Market cap</small><b>{usd(launch.marketCapUsd)}</b></span>
          <span><small>24h volume</small><b>{volumeUsd(launch.volume24hUsd, launch.volume24hEth, launch.marketCapUsd, launch.fdvEth)}</b></span>
        </span>

        <span className="market-browser-progress">
          <span><small>{graduated ? "Milestone" : "Graduation"}</small><b>{graduated ? "Complete" : `${Math.round(progress)}%`}</b></span>
          <span className="market-browser-progress-track" role="progressbar" aria-label={`${launch.symbol} graduation progress`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}>
            <i style={{ width: `${progress}%` }} />
          </span>
        </span>

        <span className="market-browser-card-foot"><span>Launched {relativeTime(launch.createdAt)} ago</span><b>Open market <i aria-hidden="true">↗</i></b></span>
      </span>
    </Link>
  );
}

export function MarketBrowser({ launches }: { launches: LaunchSummary[] }) {
  const [sort, setSort] = useState<MarketSort>("trending");
  const [view, setView] = useState<MarketView>("cards");
  const [expanded, setExpanded] = useState(false);

  const sortedLaunches = useMemo(() => [...launches].sort((a, b) => {
    if (sort === "newest") return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    if (sort === "mcap") return (b.marketCapUsd ?? 0) - (a.marketCapUsd ?? 0);
    if (sort === "volume") {
      return (volumeUsdValue(b.volume24hUsd, b.volume24hEth, b.marketCapUsd, b.fdvEth) ?? 0)
        - (volumeUsdValue(a.volume24hUsd, a.volume24hEth, a.marketCapUsd, a.fdvEth) ?? 0);
    }
    return Math.abs(b.change24h) - Math.abs(a.change24h);
  }), [launches, sort]);

  const visibleLaunches = expanded ? sortedLaunches : sortedLaunches.slice(0, 8);

  return (
    <div className="market-browser">
      <div className="market-browser-toolbar">
        <div className="market-sort-tabs" aria-label="Sort markets">
          {sortOptions.map((option) => (
            <button key={option.key} type="button" className={sort === option.key ? "is-active" : ""} aria-pressed={sort === option.key} onClick={() => setSort(option.key)}>{option.label}</button>
          ))}
        </div>
        <div className="market-view-switch" aria-label="Market display">
          <button type="button" className={view === "cards" ? "is-active" : ""} aria-pressed={view === "cards"} onClick={() => setView("cards")}><i className="view-grid-icon" aria-hidden="true" />Cards</button>
          <button type="button" className={view === "list" ? "is-active" : ""} aria-pressed={view === "list"} onClick={() => setView("list")}><i className="view-list-icon" aria-hidden="true" />List</button>
        </div>
      </div>

      <div className="market-browser-meta"><span>{visibleLaunches.length} of {sortedLaunches.length} markets</span><span>Robinhood Chain / 4663</span></div>

      {view === "cards" ? (
        <div className="market-browser-grid">
          {visibleLaunches.map((launch, index) => <MarketCard key={launch.address} launch={launch} index={index} />)}
          {visibleLaunches.length === 0 && <p className="market-browser-empty">No markets match this view.</p>}
        </div>
      ) : <MarketTable launches={visibleLaunches} compactMode />}

      {sortedLaunches.length > 8 && (
        <button className="market-browser-more" type="button" onClick={() => setExpanded((value) => !value)}>{expanded ? "Show fewer markets" : `Show all ${sortedLaunches.length} markets`}<span aria-hidden="true">{expanded ? "↑" : "↓"}</span></button>
      )}
    </div>
  );
}
